import { useState } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { CalendarPlus, CheckCircle2, FileText, Layers, Loader2, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { graphql } from "@/__generated__"
import { graphqlClient } from "@/lib/graphql"
import { useLessonVocabularyEntries } from "@/lib/vocabulary-entries"

const POST_LESSON_QUERY = graphql(`
  query PostLesson($id: ID!) {
    lesson(id: $id) {
      id
      canReview
      review { id }
      tutorProfile { id user { fullName } }
      studentProfile { id user { fullName } }
    }
  }
`)

const SUBMIT_REVIEW = graphql(`
  mutation SubmitLessonReviewFromClassroom($input: SubmitLessonReviewInput!) {
    submitLessonReview(input: $input) {
      review { id }
      errors
    }
  }
`)

const postLessonKey = (lessonId: string) => ["postLesson", lessonId] as const

function StarRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={star === value}
            aria-label={`${star}`}
            onClick={() => onChange(star)}
            className="p-0.5 rounded-sm focus-ring"
          >
            <Star className={`w-5 h-5 ${star <= value ? "fill-foreground text-foreground" : "text-muted-foreground"}`} />
          </button>
        ))}
      </div>
    </div>
  )
}

function RatingCard({ lessonId }: { lessonId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [video, setVideo] = useState(0)
  const [audio, setAudio] = useState(0)
  const [teacher, setTeacher] = useState(0)
  const [comment, setComment] = useState("")

  const submit = useMutation({
    mutationFn: () =>
      graphqlClient.request(SUBMIT_REVIEW, {
        input: {
          lessonId,
          videoRating: video,
          audioRating: audio,
          teacherRating: teacher,
          comment: comment.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      if (data.submitLessonReview.errors.length > 0) {
        toast.error(data.submitLessonReview.errors.join(", "))
        return
      }
      toast.success(t("studentCalendar.toastReviewSuccess"))
      queryClient.invalidateQueries({ queryKey: postLessonKey(lessonId) })
      queryClient.invalidateQueries({ queryKey: ["myLessons"] })
    },
    onError: () => toast.error(t("studentCalendar.toastReviewError")),
  })

  return (
    <Card data-testid="post-lesson-rating">
      <CardContent className="p-5 space-y-4">
        <h2 className="font-display font-semibold text-foreground">{t("liveSession.postLesson.ratingTitle")}</h2>
        <StarRow label={t("studentCalendar.ratingVideoQuality")} value={video} onChange={setVideo} />
        <StarRow label={t("studentCalendar.ratingAudioQuality")} value={audio} onChange={setAudio} />
        <StarRow label={t("studentCalendar.ratingTeacherFeedback")} value={teacher} onChange={setTeacher} />
        <div className="space-y-2">
          <Label htmlFor="post-lesson-comment">{t("studentCalendar.ratingCommentLabel")}</Label>
          <Textarea
            id="post-lesson-comment"
            rows={3}
            maxLength={2000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("studentCalendar.ratingCommentPlaceholder")}
          />
        </div>
        <Button
          onClick={() => submit.mutate()}
          disabled={video < 1 || audio < 1 || teacher < 1 || submit.isPending}
          className="w-full"
        >
          {submit.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("studentCalendar.ratingDialogSubmitButton")}
        </Button>
      </CardContent>
    </Card>
  )
}

// What the classroom shows after "Leave" instead of dropping the person on the
// lesson list (#711): the rating while it is fresh (student), the words captured
// during the lesson, a way to the notes and the next lesson. The student's
// reviews depend on this moment: a rating used to need the Rate button in
// Calendar history to be found later.
export function PostLessonScreen({ lessonId, role }: { lessonId: string; role: "student" | "tutor" }) {
  const { t } = useTranslation()
  const classroomPath = `/${role}/classroom`
  const calendarPath = `/${role}/calendar`

  const { data: lesson } = useQuery({
    queryKey: postLessonKey(lessonId),
    queryFn: async () => (await graphqlClient.request(POST_LESSON_QUERY, { id: lessonId })).lesson,
  })
  const { data: entries = [] } = useLessonVocabularyEntries(lessonId)
  const captured = entries.length

  const otherName = role === "student" ? lesson?.tutorProfile.user.fullName : lesson?.studentProfile.user.fullName

  return (
    <div className="flex-1 overflow-y-auto" data-testid="post-lesson">
      <div className="max-w-md mx-auto p-6 space-y-5">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-6 h-6 text-foreground" />
          </div>
          <h1 className="text-xl font-display font-semibold text-foreground">{t("liveSession.postLesson.title")}</h1>
          {otherName && <p className="text-sm text-muted-foreground">{t("liveSession.postLesson.subtitle", { name: otherName })}</p>}
        </div>

        {role === "student" && lesson && (
          lesson.review ? (
            <p className="text-sm text-center text-muted-foreground" data-testid="post-lesson-rated">{t("liveSession.postLesson.ratingThanks")}</p>
          ) : lesson.canReview ? (
            <RatingCard lessonId={lessonId} />
          ) : (
            <p className="text-sm text-center text-muted-foreground" data-testid="post-lesson-rate-later">{t("liveSession.postLesson.ratingLater")}</p>
          )
        )}

        {captured > 0 && (
          <Card>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm text-foreground" data-testid="post-lesson-captured">
                <Layers className="w-4 h-4 text-muted-foreground" />
                {t("liveSession.postLesson.cardsCaptured", { count: captured })}
              </span>
              {role === "student" && (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/student/flashcards">{t("liveSession.postLesson.reviewCards")}</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col gap-2">
          {role === "student" && lesson && (
            <Button asChild>
              <Link to={`/book/${lesson.tutorProfile.id}`}>
                <CalendarPlus className="w-4 h-4 mr-2" />{t("liveSession.postLesson.bookNext")}
              </Link>
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to={calendarPath}>
              <FileText className="w-4 h-4 mr-2" />{t("liveSession.postLesson.viewNotes")}
            </Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to={classroomPath}>{t("liveSession.backToClassroom")}</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
